import React, { useState, useEffect, useContext } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import BrowseLayout from '../components/home/BrowseLayout';
import ListingsGrid from '../components/home/ListingsGrid';
import EvolutionShelves from '../components/home/EvolutionShelves';
import { LoadingContext } from '../App';
import { sortListingsByRarity } from '../utils/sortListings';

const SUPER_LABEL = { pokemon: 'Pokémon', trainer: 'Trainers', energy: 'Energies' };

// Subcategory pages titled by the subcategory alone ("Stadiums", not
// "Trainers, Stadium"), matching the menu and search-pill names.
const SUBTYPE_LABEL = {
    'trainer:Supporter': 'Supporters',
    'trainer:Stadium': 'Stadiums',
    'trainer:Item': 'Items',
    'trainer:Pokémon Tool': 'Tools',
    'energy:Special': 'Special Energies',
    'energy:Basic': 'Basic Energies',
};

const ListingsPage = ({ supertype, isPromo }) => {
    const { name, searchTerm } = useParams();
    const [searchParams] = useSearchParams();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const { setLoading: setGlobalLoading } = useContext(LoadingContext);

    // Query-param filters (used by the homepage TypeMenu's "browse by
    // subcategory" links): supertype/subtype/series/isPromo, plus `type` (an
    // energy type like Fire, from the search dropdown's Types row).
    const qpSupertype = searchParams.get('supertype');
    const qpType = searchParams.get('type');
    const qpSubtype = searchParams.get('subtype');
    const qpSeries = searchParams.get('series');
    const qpIsPromo = searchParams.get('isPromo');

    useEffect(() => {
        const fetchListings = async () => {
            setLoading(true);
            setGlobalLoading(true);
            try {
                const params = new URLSearchParams();

                const effectiveSupertype = supertype || qpSupertype;
                if (effectiveSupertype) params.append('supertype', effectiveSupertype);
                if (searchTerm) params.append('cardName', searchTerm);
                // /artists/:name — send the artist route param as artistName.
                if (name) params.append('artistName', name);
                // Subcategory browse filters. NOTE: the backend GET /listings must
                // implement `subtype` (card subtype) and `series` (card's set series)
                // for these to actually filter — currently ignored server-side.
                if (qpSubtype) params.append('subtype', qpSubtype);
                if (qpSeries) params.append('series', qpSeries);
                if (qpType) params.append('type', qpType);
                if (isPromo || qpIsPromo) params.append('isPromo', '1');
                params.append('count', '500');

                const response = await fetch(`${apiBaseUrl}/listings?${params.toString()}`);
                if (!response.ok) throw new Error('Failed to fetch listings');
                const data = await response.json();
                setListings(data.status === 'success' && Array.isArray(data.listings) ? sortListingsByRarity(data.listings) : []);
            } catch (err) {
                setError(err.message);
                setListings([]);
            } finally {
                setLoading(false);
                setGlobalLoading(false);
            }
        };
        fetchListings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, supertype, name, isPromo, qpSupertype, qpSubtype, qpSeries, qpIsPromo, qpType]);

    // Derive the page title + subtitle from whatever filter is active.
    let title = 'Listings';
    let subtitle = `${listings.length} card${listings.length === 1 ? '' : 's'}`;
    if (searchTerm) {
        title = decodeURIComponent(searchTerm);
        subtitle = `${listings.length} card${listings.length === 1 ? '' : 's'}`;
    } else if (name) {
        title = decodeURIComponent(name);
        subtitle = `${listings.length} card${listings.length === 1 ? '' : 's'}`;
    } else if (qpSeries) {
        title = qpSeries;
    } else if (qpType) {
        title = `${qpType} Type`;
    } else {
        const st = supertype || qpSupertype;
        title = SUBTYPE_LABEL[`${st}:${qpSubtype}`]
            || (qpSubtype ? `${SUPER_LABEL[st] || 'Listings'}, ${qpSubtype}` : SUPER_LABEL[st] || 'Listings');
    }

    return (
        <BrowseLayout>
            <ListingsGrid
                title={title}
                subtitle={error ? null : subtitle}
                listings={listings}
                loading={loading}
                error={error ? `Error loading listings: ${error}` : null}
            />
            {/* Searching a Pokémon: its evolution line's listings underneath. */}
            {searchTerm && !loading && (
                <EvolutionShelves term={decodeURIComponent(searchTerm)} excludeListings={listings} />
            )}
        </BrowseLayout>
    );
};

export default ListingsPage;
